// src/finance/components/ResultCard.tsx
import { useState } from 'react'
import type { FinanceDirection, RegimeResult } from '../engine/types'
import { revenueSplit } from '../payers'
import { round2 } from '../engine/tax'
import { fmtEur } from '../format'
import { FlowBar } from './FlowBar'
import { InfoTip } from './InfoTip'
import { TAX_INFO } from './taxInfo'

function CascadeRow({
  label,
  value,
  strong,
  tone = 'neutral',
  share,
  info,
}: {
  label: string
  value: number
  strong?: boolean | undefined
  /** 'charge' = ça sort (terracotta), 'net' = ce qui reste (olive). */
  tone?: 'neutral' | 'charge' | 'net' | undefined
  /** Poids de la ligne dans le CA, pour la mini-barre (0..1). */
  share?: number | undefined
  info?: string | undefined
}) {
  // Les lignes de charge reçoivent une magnitude niée : nier 0 donne -0, que
  // Intl rend « -0 € ». On ramène -0 à 0 avant formatage.
  const displayValue = Object.is(value, -0) ? 0 : value
  const color =
    tone === 'charge'
      ? 'var(--charge)'
      : tone === 'net'
        ? 'var(--net)'
        : strong
          ? 'var(--fg)'
          : 'var(--muted)'
  const barColor =
    tone === 'charge' ? 'var(--charge)' : tone === 'net' ? 'var(--net)' : 'rgba(232, 225, 205, 0.35)'
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 76px 96px', alignItems: 'center', gap: '0.6rem', fontSize: '0.75rem' }}>
      <span style={{ color, fontWeight: strong ? 600 : 400, display: 'inline-flex', alignItems: 'center', gap: '0.25rem', minWidth: 0 }}>
        {label}
        {info === undefined ? null : <InfoTip label={label} text={info} />}
      </span>
      {/* Aucun texte ici : ResultCard.test compare une ligne en égalité exacte. */}
      <span style={{ height: '8px', background: 'rgba(232, 225, 205, 0.05)', borderRadius: '2px', overflow: 'hidden' }}>
        <i
          style={{
            display: 'block',
            height: '100%',
            borderRadius: '2px',
            background: barColor,
            width: `${Math.min(100, (share ?? 0) * 100)}%`,
          }}
        />
      </span>
      <span
        style={{
          textAlign: 'right',
          fontFamily: 'var(--mono)',
          fontVariantNumeric: 'tabular-nums',
          color,
          fontWeight: strong ? 600 : 400,
        }}
      >
        {fmtEur(displayValue)}
      </span>
    </div>
  )
}

export function ResultCard({
  title,
  regime,
  direction,
  isBest,
  personalExpensesMonthly = 0,
  unreachable = false,
  slider,
}: {
  title: string
  regime: RegimeResult
  direction: FinanceDirection
  isBest: boolean
  /**
   * true quand aucun CA n'atteint le net visé DANS CE SCÉNARIO : les montants
   * rendus sont des zéros de repli, pas des résultats. La carte le dit au lieu
   * de laisser lire un CA de 0 € — et surtout au lieu d'emprunter la bannière
   * « à perte », qui imputerait à l'utilisateur un déficit qui n'existe pas.
   */
  unreachable?: boolean | undefined
  /** Dépenses perso mensualisées : prolonge la barre jusqu'au reste réel. */
  personalExpensesMonthly?: number | undefined
  slider?:
    | {
        value: number
        onChange: (v: number) => void
        /**
         * Part salaire optimale (0..1), ou `null` si aucune part n'atteint la
         * cible. Calculée par `optimalSalaryShare` et mémoïsée par la page :
         * ce composant ne fait que la situer sur la piste.
         */
        optimum?: number | null | undefined
      }
    | undefined
}) {
  const [open, setOpen] = useState(false)
  const b = regime.breakdown
  /** Poids d'une ligne de cascade dans le CA, pour dimensionner sa mini-barre. */
  const share = (v: number) => (regime.revenueMonthly > 0 ? Math.abs(v) / regime.revenueMonthly : 0)
  /** `null` tant qu'aucun optimum n'est fourni ou calculable : rien à situer. */
  const optimumShare = slider?.optimum ?? null
  const mainLabel = direction === 'netToRevenue' ? 'CA mensuel à facturer' : 'Net perso mensuel'
  const mainValue = direction === 'netToRevenue' ? regime.revenueMonthly : regime.netMonthly
  const secondaryLabel = direction === 'netToRevenue' ? 'Net perso obtenu' : 'CA mensuel facturé'
  const secondaryValue = direction === 'netToRevenue' ? regime.netMonthly : regime.revenueMonthly

  return (
    <section
      style={{
        background: 'var(--surface)',
        border: `1px solid ${isBest ? 'var(--bronze)' : 'var(--line)'}`,
        borderRadius: '10px',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.625rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--fg)', margin: 0 }}>{title}</h3>
        {isBest && (
          <span
            style={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: 'var(--bronze)',
              border: '1px solid var(--bronze)',
              borderRadius: '4px',
              padding: '0.125rem 0.375rem',
              textTransform: 'uppercase',
              letterSpacing: '0.02em',
            }}
          >
            le plus avantageux
          </span>
        )}
      </div>

      {slider && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--muted)' }}>
            Part salaire : {Math.round(slider.value * 100)} % — dividendes : {Math.round((1 - slider.value) * 100)} %
            {/* Le repère se superpose à la piste : `position: relative` ici, et une
                graduation positionnée en pourcentage — la piste d'un input[range]
                occupe toute la largeur, donc left = part × 100 %. */}
            {/* Pas de 1 % : `optimalSalaryShare` balaie au centième, et la spec HTML
                recale la valeur d'un input[type=range] sur un multiple du pas — avec
                un pas de 5, un optimum à 38 % posait le pouce à 40 % pendant que le
                libellé et le bouton annonçaient 38 %. */}
            <span style={{ position: 'relative', display: 'block' }}>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={Math.round(slider.value * 100)}
                onChange={(e) => slider.onChange(Number(e.target.value) / 100)}
                style={{ width: '100%', margin: 0 }}
              />
              {optimumShare !== null && (
                <i
                  data-testid="slider-optimum"
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: `${Math.round(optimumShare * 100)}%`,
                    top: '-2px',
                    width: '2px',
                    height: '8px',
                    background: 'var(--bronze)',
                    pointerEvents: 'none',
                  }}
                />
              )}
            </span>
          </label>
          {optimumShare !== null && (
            <button
              type="button"
              onClick={() => slider.onChange(optimumShare)}
              style={{
                alignSelf: 'flex-start',
                background: 'transparent',
                border: '1px solid var(--bronze)',
                borderRadius: '5px',
                color: 'var(--bronze)',
                fontSize: '0.6875rem',
                fontFamily: 'inherit',
                padding: '0.1875rem 0.5rem',
                cursor: 'pointer',
              }}
            >
              Se caler sur l’optimum ({Math.round(optimumShare * 100)} %)
            </button>
          )}
        </div>
      )}

      {unreachable && (
        <div
          data-testid="result-unreachable"
          style={{ fontSize: '0.75rem', color: 'var(--arancio)', border: '1px solid var(--arancio-deep)', borderRadius: '6px', padding: '0.375rem 0.5rem' }}
        >
          Cible inatteignable dans ce scénario : aucun CA n’atteint ce net avec ces paramètres
          fiscaux. Les montants ci-dessous sont vides, pas nuls.
        </div>
      )}
      {/* « À perte » ne se dit QUE d'une perte réelle : sur un repli inatteignable,
          les zéros ne décrivent aucune situation économique. */}
      {regime.isLoss && !unreachable && (
        <div style={{ fontSize: '0.75rem', color: 'var(--arancio)', border: '1px solid var(--arancio-deep)', borderRadius: '6px', padding: '0.375rem 0.5rem' }}>
          À perte : le CA ne couvre pas les dépenses société.
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div data-testid="result-main">
          <div style={{ fontSize: '0.6875rem', color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {mainLabel}
          </div>
          <div
            style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: direction === 'revenueToNet' ? 'var(--net)' : 'var(--fg)',
              fontFamily: 'var(--mono)',
            }}
          >
            {fmtEur(mainValue)}
          </div>
        </div>
        {/* En revenueToNet, le net EST déjà le chiffre principal (et porte la couleur).
            Libellé « net d'impôts » et non « il te reste » : ce dernier appartient au
            bloc LivingBudget, qui parle du reste APRÈS les dépenses perso. */}
        {direction === 'netToRevenue' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <div style={{ fontSize: '0.6875rem', color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              net d’impôts
            </div>
            <div style={{ fontSize: '1.375rem', fontWeight: 700, color: 'var(--net)', fontFamily: 'var(--mono)' }}>
              {fmtEur(regime.netMonthly)}
            </div>
          </div>
        )}
      </div>

      <FlowBar
        segments={revenueSplit(regime, personalExpensesMonthly)}
        deficit={regime.isLoss && !unreachable ? round2(b.companyExpenses - regime.revenueMonthly) : undefined}
      />

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.75rem', color: 'var(--muted)' }}>
        {direction === 'revenueToNet' && (
          <span>{secondaryLabel} : <strong style={{ color: 'var(--fg)' }}>{fmtEur(secondaryValue)}</strong></span>
        )}
        <span data-testid="result-annual">CA annuel : <strong style={{ color: 'var(--fg)' }}>{fmtEur(regime.revenueAnnual)}</strong></span>
        <span data-testid="result-tjm">
          TJM : <strong style={{ color: 'var(--fg)' }}>{regime.tjm === null ? '—' : fmtEur(regime.tjm)}</strong>
        </span>
      </div>

      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Détail du calcul"
        style={{
          alignSelf: 'flex-start',
          background: 'transparent',
          border: 'none',
          color: 'var(--dim)',
          fontSize: '0.75rem',
          fontFamily: 'inherit',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        {open ? '▾ Masquer le détail' : '▸ Détail du calcul'}
      </button>

      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', borderTop: '1px solid var(--line)', paddingTop: '0.5rem' }}>
          <CascadeRow label="CA facturé" value={regime.revenueMonthly} strong share={1} />
          <CascadeRow label="Dépenses société" value={-b.companyExpenses} tone="charge" share={share(b.companyExpenses)} info={TAX_INFO.expenses} />
          <CascadeRow label="Enveloppe disponible" value={b.envelope} strong share={share(b.envelope)} />
          {regime.share > 0 && (
            <>
              <CascadeRow label="— Canal salaire (coût employeur)" value={b.salary.employerCost} share={share(b.salary.employerCost)} />
              <CascadeRow label="Brut" value={b.salary.gross} share={share(b.salary.gross)} />
              <CascadeRow label="Cotisations patronales" value={-b.salary.employerContrib} tone="charge" share={share(b.salary.employerContrib)} info={TAX_INFO.employerContrib} />
              <CascadeRow label="Cotisations salariales" value={-b.salary.employeeContrib} tone="charge" share={share(b.salary.employeeContrib)} info={TAX_INFO.employeeContrib} />
              <CascadeRow label="Net avant IR" value={b.salary.netBeforeIr} share={share(b.salary.netBeforeIr)} />
              <CascadeRow label="IR (barème, quotient familial)" value={-b.salary.ir} tone="charge" share={share(b.salary.ir)} info={TAX_INFO.ir} />
              <CascadeRow label="Net salaire" value={b.salary.net} strong share={share(b.salary.net)} />
            </>
          )}
          {regime.share < 1 && (
            <>
              <CascadeRow label="— Canal dividendes (bénéfice)" value={b.dividends.profitBeforeCt} share={share(b.dividends.profitBeforeCt)} />
              <CascadeRow label="Corporation Tax UK" value={-b.dividends.ct} tone="charge" share={share(b.dividends.ct)} info={TAX_INFO.corporationTaxUk} />
              <CascadeRow label="Distribuable" value={b.dividends.distributable} share={share(b.dividends.distributable)} />
              <CascadeRow label="PFU (12,8 % + 18,6 %)" value={-b.dividends.pfu} tone="charge" share={share(b.dividends.pfu)} info={TAX_INFO.pfu} />
              <CascadeRow label="Net dividendes" value={b.dividends.net} strong share={share(b.dividends.net)} />
            </>
          )}
          <CascadeRow label="Net perso total" value={regime.netMonthly} strong tone="net" share={share(regime.netMonthly)} />
        </div>
      )}
    </section>
  )
}
